<template>
  <div class="content-page">
    <page-header title="舆情内容" description="查看已采集内容、AI 分析结论和评论，风险内容请前往风险中心处理">
      <el-button
        type="primary"
        icon="el-icon-refresh"
        :loading="starting"
        :disabled="loading"
        @click="start"
      >
        {{ starting ? '启动中' : '立即采集' }}
      </el-button>
    </page-header>

    <el-card v-loading="loading" shadow="never">
      <div class="toolbar">
        <div class="toolbar-main">
          <el-select
            v-model="platform"
            clearable
            placeholder="全部平台"
            :disabled="loading"
            @change="applyFilters"
          >
            <el-option
              v-for="item in platforms"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <el-input
            v-model.trim="keyword"
            class="keyword-input"
            clearable
            prefix-icon="el-icon-search"
            placeholder="搜索标题、正文或作者"
            @input="applyFilters"
          />
          <el-button icon="el-icon-refresh-right" :loading="loading" @click="load">刷新</el-button>
        </div>
        <span class="result-hint">共 {{ filteredItems.length }} 条符合条件</span>
      </div>

      <div class="status-filter">
        <el-radio-group v-model="analysisFilter" size="small" @change="applyFilters">
          <el-radio-button label="all">全部 {{ statusCounts.all }}</el-radio-button>
          <el-radio-button label="pending">待分析 {{ statusCounts.pending }}</el-radio-button>
          <el-radio-button label="safe">无风险 {{ statusCounts.safe }}</el-radio-button>
          <el-radio-button label="low">低风险 {{ statusCounts.low }}</el-radio-button>
          <el-radio-button label="medium">中风险 {{ statusCounts.medium }}</el-radio-button>
          <el-radio-button label="high">高风险 {{ statusCounts.high }}</el-radio-button>
        </el-radio-group>
      </div>

      <el-alert
        v-if="loadError"
        class="load-error"
        type="warning"
        :title="loadError"
        :closable="false"
        show-icon
      />

      <el-table
        :data="pagedItems"
        :row-key="rowKey"
        border
        class="content-table"
      >
        <el-table-column label="内容" min-width="330">
          <template slot-scope="scope">
            <div class="content-title">{{ scope.row.title || scope.row.source_id }}</div>
            <div class="content-summary">
              {{ scope.row.content_text || scope.row.summary || '平台未返回可展示的正文摘要' }}
            </div>
            <div class="content-meta">
              <span>{{ scope.row.author_name || '作者未知' }}</span>
              <span>发布于 {{ formatDate(scope.row.published_at) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="平台" width="105" align="center">
          <template slot-scope="scope"><platform-tag :value="scope.row.platform" /></template>
        </el-table-column>
        <el-table-column label="AI 判断" width="105" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="analysisTagType(scope.row)">
              {{ analysisText(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="AI 结论" min-width="250">
          <template slot-scope="scope">
            <div :class="['analysis-summary', { pending: statusKey(scope.row) === 'pending' }]">
              {{ analysisSummary(scope.row) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="最近采集" width="165">
          <template slot-scope="scope">{{ formatDate(scope.row.latest_seen_at) }}</template>
        </el-table-column>
        <el-table-column label="评论" width="90" align="center">
          <template slot-scope="scope">
            <el-button
              type="text"
              :loading="commentsLoading && commentItemId === rowKey(scope.row)"
              :disabled="Number(scope.row.comment_count || 0) === 0 || commentsLoading"
              @click="openComments(scope.row)"
            >
              {{ Number(scope.row.comment_count || 0) }} 条
            </el-button>
          </template>
        </el-table-column>
        <el-table-column label="原文" width="80" align="center">
          <template slot-scope="scope">
            <a v-if="scope.row.source_url" :href="scope.row.source_url" target="_blank" rel="noopener">查看</a>
            <span v-else class="muted">无链接</span>
          </template>
        </el-table-column>
        <div slot="empty" class="empty-state">
          <i :class="emptyState.icon" />
          <strong>{{ emptyState.title }}</strong>
          <span>{{ emptyState.description }}</span>
        </div>
      </el-table>

      <div v-if="filteredItems.length > pageSize" class="pagination">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :current-page.sync="currentPage"
          :page-size="pageSize"
          :total="filteredItems.length"
        />
      </div>
    </el-card>

    <el-dialog :visible.sync="commentDialogVisible" :title="commentTitle" width="760px">
      <el-table v-loading="commentsLoading" :data="comments" max-height="480">
        <el-table-column label="评论内容" min-width="330">
          <template slot-scope="scope">
            <div :class="{ reply: scope.row.parent_comment_id }">
              <span v-if="scope.row.parent_comment_id" class="reply-label">回复</span>
              {{ scope.row.content }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="author_name" label="评论者" width="130" />
        <el-table-column label="发布时间" width="170">
          <template slot-scope="scope">{{ formatDate(scope.row.published_at) }}</template>
        </el-table-column>
        <el-table-column label="来源" width="80">
          <template slot-scope="scope">
            <a v-if="scope.row.comment_url" :href="scope.row.comment_url" target="_blank" rel="noopener">查看</a>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <div slot="empty" class="dialog-empty">该内容当前没有采集到公开评论</div>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import { yuqingApi, loadHistoricalContents, loadHistoricalComments } from '@/api/yuqing'
import PageHeader from '../components/PageHeader.vue'
import PlatformTag from '../components/PlatformTag.vue'

export default {
  components: { PageHeader, PlatformTag },
  data () {
    return {
      loading: true,
      starting: false,
      items: [],
      loadError: '',
      platform: '',
      keyword: '',
      analysisFilter: 'all',
      currentPage: 1,
      pageSize: 20,
      comments: [],
      commentsLoading: false,
      commentItemId: '',
      commentDialogVisible: false,
      commentTitle: '已采集评论',
      platforms: [
        { label: 'B站', value: 'bilibili' },
        { label: '抖音', value: 'douyin' },
        { label: '快手', value: 'kuaishou' },
        { label: '小红书', value: 'xiaohongshu' },
        { label: '微博', value: 'weibo' },
        { label: '视频号', value: 'wechat_channels' },
        { label: '微信公众号', value: 'wechat_mp' },
        { label: '知乎', value: 'zhihu' }
      ]
    }
  },
  computed: {
    filteredItems () {
      const keyword = this.keyword.toLowerCase()
      return this.items.filter(item => {
        const text = [item.title, item.content_text, item.author_name, item.source_id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return (!this.platform || item.platform === this.platform) &&
          (this.analysisFilter === 'all' || this.statusKey(item) === this.analysisFilter) &&
          (!keyword || text.indexOf(keyword) >= 0)
      })
    },
    pagedItems () {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredItems.slice(start, start + this.pageSize)
    },
    statusCounts () {
      const keyword = this.keyword.toLowerCase()
      const scopedItems = this.items.filter(item => {
        const text = [item.title, item.content_text, item.author_name, item.source_id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return (!this.platform || item.platform === this.platform) &&
          (!keyword || text.indexOf(keyword) >= 0)
      })
      const counts = { all: scopedItems.length, pending: 0, safe: 0, low: 0, medium: 0, high: 0 }
      scopedItems.forEach(item => {
        const key = this.statusKey(item)
        if (Object.prototype.hasOwnProperty.call(counts, key)) counts[key] += 1
      })
      return counts
    },
    emptyState () {
      if (this.items.length > 0) {
        return {
          icon: 'el-icon-search',
          title: '当前筛选条件下没有内容',
          description: `系统已有 ${this.items.length} 条采集内容，请调整平台、分析状态或搜索词。`
        }
      }
      return {
        icon: 'el-icon-document',
        title: '还没有采集到可展示的内容',
        description: '可点击“立即采集”；采集、评论获取和 AI 分析需要一点时间。'
      }
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      try {
        const items = await loadHistoricalContents()
        this.items = items.filter(item => !this.isMalformedWeibo(item))
        this.loadError = ''
        this.applyFilters()
      } catch (error) {
        this.loadError = error.message || '内容加载失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    async start () {
      if (this.starting) return
      this.starting = true
      try {
        await yuqingApi.post('public-opinion/refresh', {})
        this.$message.success('采集任务已启动，请稍后点击“刷新”查看最新结果')
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.starting = false
      }
    },
    applyFilters () {
      this.currentPage = 1
    },
    async openComments (item) {
      if (this.commentsLoading) return
      this.commentDialogVisible = true
      this.commentsLoading = true
      this.commentItemId = this.rowKey(item)
      this.comments = []
      this.commentTitle = `${item.title || item.source_id} · 已采集评论`
      try {
        this.comments = await loadHistoricalComments(item.platform, item.source_id)
      } catch (error) {
        this.$message.error(error.message || '评论加载失败，请稍后重试')
      } finally {
        this.commentsLoading = false
        this.commentItemId = ''
      }
    },
    rowKey (item) {
      return item.id || `${item.platform}:${item.source_id}`
    },
    statusKey (item) {
      if (!item.analysis_type) return 'pending'
      if (!this.booleanValue(item.is_lechun_negative)) return 'safe'
      const level = String(item.risk_level || '').toLowerCase()
      if (level === 'high' || level === '高') return 'high'
      if (level === 'medium' || level === '中') return 'medium'
      if (level === 'low' || level === '低') return 'low'
      return 'pending'
    },
    analysisText (item) {
      return {
        pending: '待分析',
        safe: '无风险',
        low: '低风险',
        medium: '中风险',
        high: '高风险'
      }[this.statusKey(item)]
    },
    analysisTagType (item) {
      return {
        pending: 'info',
        safe: 'success',
        low: '',
        medium: 'warning',
        high: 'danger'
      }[this.statusKey(item)]
    },
    analysisSummary (item) {
      if (item.conclusion_summary) return item.conclusion_summary
      return this.statusKey(item) === 'pending' ? '已采集，正在等待 AI 语义分析' : 'AI 未返回补充说明'
    },
    booleanValue (value) {
      return value === true || value === 1 || value === '1'
    },
    isMalformedWeibo (item) {
      const text = String(item.content_text || item.title || '')
      return item.platform === 'weibo' && text.indexOf('"icon_url"') >= 0 && text.indexOf('"text":"公开"') >= 0
    },
    formatDate (value) {
      if (value === null || value === undefined || value === '') return '-'
      const numeric = Number(value)
      const date = Number.isFinite(numeric)
        ? new Date(numeric < 100000000000 ? numeric * 1000 : numeric)
        : new Date(value)
      return Number.isNaN(date.getTime())
        ? String(value)
        : date.toLocaleString('zh-CN', { hour12: false })
    }
  }
}
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}
.toolbar-main {
  display: flex;
  align-items: center;
  gap: 10px;
}
.toolbar .el-select {
  width: 170px;
}
.keyword-input {
  width: 260px;
}
.result-hint,
.muted {
  color: #909399;
  font-size: 13px;
}
.status-filter {
  padding: 12px 0 16px;
  border-top: 1px solid #ebeef5;
}
.load-error {
  margin-bottom: 12px;
}
.content-table {
  width: 100%;
}
.content-title {
  color: #303133;
  font-weight: 600;
  line-height: 1.5;
}
.content-summary,
.analysis-summary {
  display: -webkit-box;
  overflow: hidden;
  margin-top: 5px;
  color: #606266;
  line-height: 1.55;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.analysis-summary.pending {
  color: #909399;
}
.content-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 7px;
  color: #909399;
  font-size: 12px;
}
.pagination {
  display: flex;
  justify-content: flex-end;
  padding-top: 18px;
}
.empty-state {
  display: flex;
  min-height: 180px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
}
.empty-state i {
  margin-bottom: 12px;
  color: #c0c4cc;
  font-size: 32px;
}
.empty-state strong {
  margin-bottom: 6px;
  color: #606266;
  font-size: 15px;
  font-weight: 500;
}
.empty-state span,
.dialog-empty {
  color: #909399;
  font-size: 13px;
}
.dialog-empty {
  padding: 36px 0;
  text-align: center;
}
.reply {
  padding-left: 18px;
  color: #606266;
}
.reply-label {
  display: inline-block;
  margin-right: 6px;
  padding: 1px 5px;
  border-radius: 3px;
  background: #f4f4f5;
  color: #909399;
  font-size: 12px;
}
@media (max-width: 1100px) {
  .toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
  .toolbar-main {
    width: 100%;
    flex-wrap: wrap;
  }
  .status-filter {
    overflow-x: auto;
    white-space: nowrap;
  }
}
</style>
